/**
 * Lightweight client-side spam & duplicate-submission protection.
 *
 * Three layers:
 *  1. Honeypot — a hidden input most bots fill in. If non-empty, drop silently.
 *  2. Time trap — humans take >3s to fill a form; bots submit instantly.
 *  3. Duplicate guard — same payload submitted twice from the same browser
 *     within DUPLICATE_WINDOW_MS is blocked.
 *
 * NOTE: this is not bulletproof — it's friction, not a firewall. Real abuse
 * mitigation belongs at the backend / WAF layer.
 */

const STORAGE_PREFIX = "tms.formhash.";
const DUPLICATE_WINDOW_MS = 10 * 60 * 1000; // 10 minutes
const MIN_FILL_MS = 3000;

export const HONEYPOT_FIELD_NAME = "website_url"; // looks tempting to bots

export type SpamCheckResult =
  | { ok: true }
  | { ok: false; reason: "honeypot" | "too_fast" | "duplicate"; message?: string };

/** Drop-in hidden honeypot input. Render once inside every <form>. */
export function honeypotInputProps() {
  return {
    type: "text" as const,
    name: HONEYPOT_FIELD_NAME,
    tabIndex: -1,
    autoComplete: "off",
    "aria-hidden": true,
    style: {
      position: "absolute" as const,
      left: "-10000px",
      width: "1px",
      height: "1px",
      opacity: 0,
      pointerEvents: "none" as const,
    },
  };
}

async function hashPayload(formId: string, payload: Record<string, unknown>): Promise<string> {
  const json = JSON.stringify({ formId, payload });
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const buf = new TextEncoder().encode(json);
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
  // Fallback (non-secure): simple string hash
  let h = 0;
  for (let i = 0; i < json.length; i++) h = (h * 31 + json.charCodeAt(i)) | 0;
  return `${formId}:${h}`;
}

interface CheckOpts {
  formId: string;
  formData: FormData;
  startedAt: number;
  /** A normalized record of submitted values used for duplicate detection. */
  payload: Record<string, unknown>;
}

export async function checkSubmission(opts: CheckOpts): Promise<SpamCheckResult> {
  // 1. Honeypot
  const trap = String(opts.formData.get(HONEYPOT_FIELD_NAME) || "");
  if (trap.trim().length > 0) {
    return { ok: false, reason: "honeypot" };
  }

  // 2. Time trap
  const elapsed = Date.now() - opts.startedAt;
  if (elapsed < MIN_FILL_MS) {
    return {
      ok: false,
      reason: "too_fast",
      message: "Hold on a moment — please review your details and submit again.",
    };
  }

  // 3. Duplicate guard
  if (typeof window !== "undefined") {
    try {
      const hash = await hashPayload(opts.formId, opts.payload);
      const key = `${STORAGE_PREFIX}${opts.formId}`;
      const existing = window.sessionStorage.getItem(key);
      if (existing) {
        const parsed = JSON.parse(existing) as { hash: string; ts: number };
        if (parsed.hash === hash && Date.now() - parsed.ts < DUPLICATE_WINDOW_MS) {
          return {
            ok: false,
            reason: "duplicate",
            message:
              "Looks like we already received this exact request — we'll be in touch shortly. Need to send something different? Change a field and try again.",
          };
        }
      }
      window.sessionStorage.setItem(key, JSON.stringify({ hash, ts: Date.now() }));
    } catch {
      /* ignore storage errors */
    }
  }

  return { ok: true };
}
